import React, { useState } from 'react';
import { API_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, Check, Building2, Building, Sparkles, Layers } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const BLOCK_SCOPE_OPTIONS = [
  { key: 'stairwells_landings', label: 'Stairwells & landings' },
  { key: 'communal_corridors', label: 'Communal corridors' },
  { key: 'entrances_lobbies', label: 'Entrances & lobbies' },
  { key: 'lifts', label: 'Lifts' },
  { key: 'bin_areas', label: 'Bin areas' },
  { key: 'cobweb_high_level_dusting', label: 'Cobweb & high-level dusting' },
  { key: 'glass_internal_doors', label: 'Glass & internal entrance doors' },
  { key: 'external_litter_grounds', label: 'External litter picking / grounds' },
];

const OFFICE_SCOPE_OPTIONS = [
  { key: 'general_office_desks_floors', label: 'General office cleaning (desks, floors, bins)' },
  { key: 'kitchen_breakroom', label: 'Kitchen & breakroom' },
  { key: 'bathrooms_wc', label: 'Bathrooms & WC' },
  { key: 'meeting_rooms', label: 'Meeting & conference rooms' },
  { key: 'reception_area', label: 'Reception area' },
  { key: 'window_cleaning_internal', label: 'Window cleaning (internal)' },
  { key: 'window_cleaning_external', label: 'Window cleaning (external)' },
  { key: 'carpet_cleaning', label: 'Carpet cleaning' },
  { key: 'deep_cleaning', label: 'Deep cleaning' },
  { key: 'waste_recycling', label: 'Waste & recycling management' },
  { key: 'sanitisation_disinfection', label: 'Sanitisation & disinfection' },
];

const WINDOW_SCOPE_OPTIONS = [
  { key: 'external_windows', label: 'External windows' },
  { key: 'internal_windows', label: 'Internal windows' },
  { key: 'frames_sills', label: 'Window frames & sills' },
  { key: 'conservatory_roof', label: 'Conservatory / glass roof' },
  { key: 'skylights', label: 'Skylights & roof lights' },
  { key: 'glass_doors', label: 'Glass doors & panels' },
  { key: 'fascia_soffits', label: 'Fascia & soffits' },
  { key: 'gutters', label: 'Gutter cleaning' },
  { key: 'cladding', label: 'Cladding cleaning' },
  { key: 'pressure_washing', label: 'Pressure washing (paths/patios)' },
];

// Floor Restoration Options
const FLOOR_TYPE_OPTIONS = [
  { value: 'vinyl', label: 'Vinyl' },
  { value: 'lino', label: 'Lino' },
  { value: 'hard_floor', label: 'Hard Floor' },
  { value: 'unknown', label: 'Unknown' },
];

const FLOOR_CONDITION_OPTIONS = [
  { value: 'light_wear', label: 'Light Wear' },
  { value: 'moderate_wear', label: 'Moderate Wear' },
  { value: 'heavy_wear', label: 'Heavy Wear' },
  { value: 'requires_full_strip', label: 'Requires Full Strip' },
];

const FLOOR_FREQUENCY_OPTIONS = [
  { value: 'one_off', label: 'One-off' },
  { value: 'annual', label: 'Annual' },
  { value: 'other', label: 'Other' },
];

const FLOOR_AREAS_OPTIONS = [
  { key: 'staircases', label: 'Staircases' },
  { key: 'landings', label: 'Landings' },
  { key: 'entrances', label: 'Entrances' },
  { key: 'corridors', label: 'Corridors' },
  { key: 'bin_store', label: 'Bin Store' },
];

const FLOOR_SERVICE_OPTIONS = [
  { value: 'deep_clean', label: 'Deep Clean Only', margin: { min: 20, max: 25 } },
  { value: 'strip_polish', label: 'Strip & Polish (Standard)', margin: { min: 25, max: 30 } },
  { value: 'pu_coating', label: 'Full Protection System (PU Coating)', margin: { min: 30, max: 40 }, recommended: true },
];

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'every_other_day', label: 'Every other day' },
  { value: 'twice_weekly', label: 'Twice weekly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
];

const WINDOW_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'every_6_weeks', label: 'Every 6 weeks' },
  { value: 'every_8_weeks', label: 'Every 8 weeks' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'one_off', label: 'One-off clean' },
];

const QuoteCreate = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    // Quote Type
    quote_type: 'block', // block / office / window / floor
    
    // Client & Site
    company_name: '',
    contact_name: '',
    email: '',
    site_address: '',
    number_of_blocks: '',
    
    // Service
    cleaning_frequency: 'weekly',
    scope_of_works: {
      stairwells_landings: true,
      communal_corridors: true,
      entrances_lobbies: true,
      lifts: false,
      bin_areas: false,
      cobweb_high_level_dusting: false,
      glass_internal_doors: false,
      external_litter_grounds: false,
    },
    office_scope_of_works: {
      general_office_desks_floors: true,
      kitchen_breakroom: true,
      bathrooms_wc: true,
      meeting_rooms: false,
      reception_area: false,
      window_cleaning_internal: false,
      window_cleaning_external: false,
      carpet_cleaning: false,
      deep_cleaning: false,
      waste_recycling: false,
      sanitisation_disinfection: false,
    },
    window_scope_of_works: {
      external_windows: true,
      internal_windows: false,
      frames_sills: true,
      conservatory_roof: false,
      skylights: false,
      glass_doors: false,
      fascia_soffits: false,
      gutters: false,
      cladding: false,
      pressure_washing: false,
    },
    
    // Floor Restoration Fields
    floor_details: {
      // Site Details
      property_name: '',
      num_blocks: '',
      floors_per_block: '',
      total_flats: '',
      total_floor_area: '',
      access_notes: '',
      // Floor Details
      floor_type: 'vinyl',
      condition: 'moderate_wear',
      last_treatment: '',
      // Scope of Work
      cleaning_frequency: 'one_off',
      areas_included: {
        staircases: true,
        landings: true,
        entrances: true,
        corridors: true,
        bin_store: false,
      },
      additional_areas: '',
      // Service Type
      service_type: 'strip_polish',
      // Pricing (internal)
      subcontractor_cost: '',
      additional_costs: '',
      labour_days: '',
    },
    
    // Pricing
    monthly_price: '',
    per_clean_price: '',  // For window cleaning quotes
    pricing_type: 'monthly',  // 'monthly' or 'per_clean' (window only)
    vat_registered: false,
    
    // Terms
    contract_type: 'rolling_monthly',
    notice_period: 30,
    start_timeframe: 'within 7–14 days',
    
    // Optional
    notes: '',
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFloorInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      floor_details: {
        ...prev.floor_details,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  const handleFloorAreaChange = (key) => {
    setFormData(prev => ({
      ...prev,
      floor_details: {
        ...prev.floor_details,
        areas_included: {
          ...prev.floor_details.areas_included,
          [key]: !prev.floor_details.areas_included[key]
        }
      }
    }));
  };

  const handleScopeChange = (key, scopeType = 'block') => {
    const scopeField = scopeType === 'office' ? 'office_scope_of_works' : 
                       scopeType === 'window' ? 'window_scope_of_works' : 'scope_of_works';
    setFormData(prev => ({
      ...prev,
      [scopeField]: {
        ...prev[scopeField],
        [key]: !prev[scopeField][key]
      }
    }));
  };

  // Calculate floor restoration pricing
  const calculateFloorPricing = () => {
    const { subcontractor_cost, additional_costs, service_type } = formData.floor_details;
    const subCost = parseFloat(subcontractor_cost) || 0;
    const addCost = parseFloat(additional_costs) || 0;
    const totalCost = subCost + addCost;
    
    // Get margin based on service type
    const serviceOption = FLOOR_SERVICE_OPTIONS.find(opt => opt.value === service_type);
    const marginMin = serviceOption?.margin?.min || 25;
    const marginMax = serviceOption?.margin?.max || 30;
    const marginAvg = (marginMin + marginMax) / 2;
    
    // Calculate suggested price with margin
    const suggestedPrice = totalCost > 0 ? Math.ceil(totalCost * (1 + marginAvg / 100)) : 0;
    
    return {
      totalCost,
      suggestedPrice,
      marginMin,
      marginMax,
      marginAvg
    };
  };

  const validateForm = () => {
    if (!formData.company_name.trim()) return 'Company name is required';
    if (!formData.contact_name.trim()) return 'Contact name is required';
    if (!formData.email.trim()) return 'Email is required';
    
    // Floor restoration has different required fields
    if (formData.quote_type === 'floor') {
      const fd = formData.floor_details;
      if (!fd.property_name.trim()) return 'Property name/address is required';
      if (!fd.num_blocks || parseInt(fd.num_blocks) <= 0) return 'Number of blocks is required';
      if (!fd.floors_per_block || parseInt(fd.floors_per_block) <= 0) return 'Floors per block is required';
      if (!fd.total_floor_area || parseFloat(fd.total_floor_area) <= 0) return 'Total floor area is required';
      if (!fd.subcontractor_cost || parseFloat(fd.subcontractor_cost) <= 0) return 'Subcontractor cost is required';
      return null;
    }
    
    if (!formData.site_address.trim()) return 'Site address is required';
    if (!formData.number_of_blocks.trim()) {
      if (formData.quote_type === 'office') return 'Office size/floors is required';
      if (formData.quote_type === 'window') return 'Property details is required';
      return 'Number of blocks is required';
    }
    // Window quotes can use per_clean_price instead of monthly_price
    if (formData.quote_type === 'window' && formData.pricing_type === 'per_clean') {
      if (!formData.per_clean_price || parseFloat(formData.per_clean_price) <= 0) return 'Price per clean is required';
    } else {
      if (!formData.monthly_price || parseFloat(formData.monthly_price) <= 0) return 'Monthly price is required';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Build a clean payload with only the fields relevant to this quote type
      const basePayload = {
        quote_type: formData.quote_type,
        company_name: formData.company_name,
        contact_name: formData.contact_name,
        email: formData.email,
        site_address: formData.site_address,
        number_of_blocks: formData.number_of_blocks,
        cleaning_frequency: formData.cleaning_frequency,
        monthly_price: formData.monthly_price ? parseFloat(formData.monthly_price) : 0,
        per_clean_price: formData.per_clean_price ? parseFloat(formData.per_clean_price) : 0,
        pricing_type: formData.pricing_type,
        vat_registered: formData.vat_registered,
        contract_type: formData.contract_type,
        notice_period: parseInt(formData.notice_period),
        start_timeframe: formData.start_timeframe,
        notes: formData.notes,
      };

      // Only include scope relevant to the quote type
      if (formData.quote_type === 'block') {
        basePayload.scope_of_works = formData.scope_of_works;
      } else if (formData.quote_type === 'office') {
        basePayload.office_scope_of_works = formData.office_scope_of_works;
      } else if (formData.quote_type === 'window') {
        basePayload.window_scope_of_works = formData.window_scope_of_works;
      } else if (formData.quote_type === 'floor') {
        const pricing = calculateFloorPricing();
        basePayload.floor_details = {
          ...formData.floor_details,
          subcontractor_cost: parseFloat(formData.floor_details.subcontractor_cost) || 0,
          additional_costs: parseFloat(formData.floor_details.additional_costs) || 0,
          labour_days: parseInt(formData.floor_details.labour_days) || 0,
          num_blocks: parseInt(formData.floor_details.num_blocks) || 0,
          floors_per_block: parseInt(formData.floor_details.floors_per_block) || 0,
          total_flats: parseInt(formData.floor_details.total_flats) || 0,
          total_floor_area: parseFloat(formData.floor_details.total_floor_area) || 0,
          total_cost: pricing.totalCost,
          suggested_price: pricing.suggestedPrice,
          margin_applied: pricing.marginAvg,
        };
        basePayload.site_address = formData.floor_details.property_name;
        basePayload.number_of_blocks = `${formData.floor_details.num_blocks} blocks`;
        basePayload.cleaning_frequency = formData.floor_details.cleaning_frequency || 'one_off';
      }
      
      const response = await fetch(`${API_URL}/api/admin/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(basePayload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        navigate(`/admin/quotes/${data.quote.id}`);
      } else {
        // Handle validation errors (array) vs simple error strings
        const errorMsg = Array.isArray(data.detail) 
          ? data.detail.map(e => e.msg).join(', ') 
          : (data.detail || 'Failed to create quote');
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error creating quote:', err);
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isOffice = formData.quote_type === 'office';
  const isWindow = formData.quote_type === 'window';
  const isFloor = formData.quote_type === 'floor';
  const currentScopeOptions = isWindow ? WINDOW_SCOPE_OPTIONS : isOffice ? OFFICE_SCOPE_OPTIONS : BLOCK_SCOPE_OPTIONS;
  const currentScope = isWindow ? formData.window_scope_of_works : isOffice ? formData.office_scope_of_works : formData.scope_of_works;
  const currentFrequencyOptions = isWindow ? WINDOW_FREQUENCY_OPTIONS : FREQUENCY_OPTIONS;
  const floorPricing = isFloor ? calculateFloorPricing() : null;

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Create Quote</h1>
          <p className="text-gray-500 text-sm mt-1">Fill in the details to generate a new quote</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Quote Type Selector */}
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Quote Type</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label
              className={`
                flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all
                ${formData.quote_type === 'block'
                  ? 'border-[#1a1f4e] bg-[#1a1f4e]/5'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <input
                type="radio"
                name="quote_type"
                value="block"
                checked={formData.quote_type === 'block'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center
                ${formData.quote_type === 'block' ? 'bg-[#1a1f4e] text-white' : 'bg-gray-100 text-gray-500'}
              `}>
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Communal Block</p>
                <p className="text-sm text-gray-500">Flats, apartments</p>
              </div>
            </label>
            
            <label
              className={`
                flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all
                ${formData.quote_type === 'office'
                  ? 'border-[#1a1f4e] bg-[#1a1f4e]/5'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <input
                type="radio"
                name="quote_type"
                value="office"
                checked={formData.quote_type === 'office'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center
                ${formData.quote_type === 'office' ? 'bg-[#1a1f4e] text-white' : 'bg-gray-100 text-gray-500'}
              `}>
                <Building className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Office</p>
                <p className="text-sm text-gray-500">Commercial offices</p>
              </div>
            </label>
            
            <label
              className={`
                flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all
                ${formData.quote_type === 'window'
                  ? 'border-[#00c853] bg-[#00c853]/5'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <input
                type="radio"
                name="quote_type"
                value="window"
                checked={formData.quote_type === 'window'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center
                ${formData.quote_type === 'window' ? 'bg-[#00c853] text-white' : 'bg-gray-100 text-gray-500'}
              `}>
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">External Windows</p>
                <p className="text-sm text-gray-500">Window cleaning</p>
              </div>
            </label>
            
            <label
              className={`
                flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all
                ${formData.quote_type === 'floor'
                  ? 'border-amber-500 bg-amber-500/5'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <input
                type="radio"
                name="quote_type"
                value="floor"
                checked={formData.quote_type === 'floor'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center
                ${formData.quote_type === 'floor' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-500'}
              `}>
                <Layers className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Floor Restoration</p>
                <p className="text-sm text-gray-500">Strip & Polish</p>
              </div>
            </label>
          </div>
        </section>

        {/* Client & Site Details - Show for non-floor quotes */}
        {!isFloor && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Client & Site Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="company_name" className="text-gray-700 font-medium mb-2 block">
                Company name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                className="h-11 rounded-xl"
                placeholder="e.g., ABC Property Management"
                data-testid="quote-company-name"
              />
            </div>
            
            <div>
              <Label htmlFor="contact_name" className="text-gray-700 font-medium mb-2 block">
                Contact name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="contact_name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleInputChange}
                className="h-11 rounded-xl"
                placeholder="e.g., John Smith"
                data-testid="quote-contact-name"
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">
                Email address <span className="text-red-400">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className="h-11 rounded-xl"
                placeholder="e.g., john@company.com"
                data-testid="quote-email"
              />
            </div>
            
            <div className="md:col-span-2">
              <Label htmlFor="site_address" className="text-gray-700 font-medium mb-2 block">
                Site address (full) <span className="text-red-400">*</span>
              </Label>
              <Input
                id="site_address"
                name="site_address"
                value={formData.site_address}
                onChange={handleInputChange}
                className="h-11 rounded-xl"
                placeholder={isWindow 
                  ? "e.g., 45 High Street, Edgware, HA8 7AB"
                  : isOffice 
                    ? "e.g., Suite 200, Business Park, 45 High Street, London, EC1A 1AA" 
                    : "e.g., Maple Court, 123 High Street, Edgware, HA8 7AB"
                }
                data-testid="quote-site-address"
              />
            </div>
            
            <div>
              <Label htmlFor="number_of_blocks" className="text-gray-700 font-medium mb-2 block">
                {isWindow ? 'Property details' : isOffice ? 'Office size / floors' : 'Number of blocks / entrances'} <span className="text-red-400">*</span>
              </Label>
              <Input
                id="number_of_blocks"
                name="number_of_blocks"
                value={formData.number_of_blocks}
                onChange={handleInputChange}
                className="h-11 rounded-xl"
                placeholder={isWindow 
                  ? "e.g., 3-storey house, 12 windows"
                  : isOffice 
                    ? "e.g., 3 floors, 5000 sqft" 
                    : "e.g., 2 blocks, 4 entrances"
                }
                data-testid="quote-blocks"
              />
            </div>
          </div>
        </section>
        )}

        {/* Floor Restoration Form */}
        {isFloor && (
          <>
            {/* Client Details */}
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Client Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company_name" className="text-gray-700 font-medium mb-2 block">
                    Company name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., ABC Property Management"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name" className="text-gray-700 font-medium mb-2 block">
                    Contact name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., John Smith"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">
                    Email <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., john@company.com"
                  />
                </div>
              </div>
            </section>

            {/* Site Details */}
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Site Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label htmlFor="property_name" className="text-gray-700 font-medium mb-2 block">
                    Property Name / Address <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="property_name"
                    name="property_name"
                    value={formData.floor_details.property_name}
                    onChange={handleFloorInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., Maple Court, 123 High Street, London N1 2AB"
                  />
                </div>
                <div>
                  <Label htmlFor="num_blocks" className="text-gray-700 font-medium mb-2 block">
                    Number of Blocks / Entrances <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="num_blocks"
                    name="num_blocks"
                    type="number"
                    min="1"
                    value={formData.floor_details.num_blocks}
                    onChange={handleFloorInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., 2"
                  />
                </div>
                <div>
                  <Label htmlFor="floors_per_block" className="text-gray-700 font-medium mb-2 block">
                    Floors per Block <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="floors_per_block"
                    name="floors_per_block"
                    type="number"
                    min="1"
                    value={formData.floor_details.floors_per_block}
                    onChange={handleFloorInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., 5"
                  />
                </div>
                <div>
                  <Label htmlFor="total_flats" className="text-gray-700 font-medium mb-2 block">
                    Total Flats <span className="text-gray-400">(optional)</span>
                  </Label>
                  <Input
                    id="total_flats"
                    name="total_flats"
                    type="number"
                    min="0"
                    value={formData.floor_details.total_flats}
                    onChange={handleFloorInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., 24"
                  />
                </div>
                <div>
                  <Label htmlFor="total_floor_area" className="text-gray-700 font-medium mb-2 block">
                    Estimated Total Floor Area (m²) <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="total_floor_area"
                    name="total_floor_area"
                    type="number"
                    min="0"
                    step="0.1"
                    value={formData.floor_details.total_floor_area}
                    onChange={handleFloorInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., 250"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="access_notes" className="text-gray-700 font-medium mb-2 block">
                    Access Notes
                  </Label>
                  <textarea
                    id="access_notes"
                    name="access_notes"
                    value={formData.floor_details.access_notes}
                    onChange={handleFloorInputChange}
                    className="w-full h-24 px-4 py-3 rounded-xl border border-gray-200 text-sm resize-none"
                    placeholder="e.g., Key held by concierge, best access times, parking restrictions..."
                  />
                </div>
              </div>
            </section>

            {/* Floor Details */}
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Floor Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="floor_type" className="text-gray-700 font-medium mb-2 block">
                    Floor Type
                  </Label>
                  <select
                    id="floor_type"
                    name="floor_type"
                    value={formData.floor_details.floor_type}
                    onChange={handleFloorInputChange}
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 text-sm"
                  >
                    {FLOOR_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="condition" className="text-gray-700 font-medium mb-2 block">
                    Condition
                  </Label>
                  <select
                    id="condition"
                    name="condition"
                    value={formData.floor_details.condition}
                    onChange={handleFloorInputChange}
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 text-sm"
                  >
                    {FLOOR_CONDITION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="last_treatment" className="text-gray-700 font-medium mb-2 block">
                    Last Treatment <span className="text-gray-400">(optional)</span>
                  </Label>
                  <Input
                    id="last_treatment"
                    name="last_treatment"
                    value={formData.floor_details.last_treatment}
                    onChange={handleFloorInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., 2 years ago"
                  />
                </div>
              </div>
            </section>

            {/* Scope of Work */}
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Scope of Work</h2>
              
              <div className="mb-6">
                <Label className="text-gray-700 font-medium mb-3 block">
                  Cleaning Frequency Required
                </Label>
                <div className="flex flex-wrap gap-3">
                  {FLOOR_FREQUENCY_OPTIONS.map((freq) => (
                    <label
                      key={freq.value}
                      className={`
                        flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors
                        ${formData.floor_details.cleaning_frequency === freq.value
                          ? 'border-amber-500 bg-amber-500/5 text-amber-700'
                          : 'border-gray-200 hover:border-gray-300'
                        }
                      `}
                    >
                      <input
                        type="radio"
                        name="cleaning_frequency"
                        value={freq.value}
                        checked={formData.floor_details.cleaning_frequency === freq.value}
                        onChange={handleFloorInputChange}
                        className="sr-only"
                      />
                      <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        formData.floor_details.cleaning_frequency === freq.value ? 'border-amber-500' : 'border-gray-300'
                      }`}>
                        {formData.floor_details.cleaning_frequency === freq.value && (
                          <span className="w-2 h-2 rounded-full bg-amber-500" />
                        )}
                      </span>
                      {freq.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <Label className="text-gray-700 font-medium mb-3 block">
                  Areas Included
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {FLOOR_AREAS_OPTIONS.map((option) => (
                    <label
                      key={option.key}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors
                        ${formData.floor_details.areas_included[option.key]
                          ? 'border-amber-500 bg-amber-500/5'
                          : 'border-gray-200 hover:border-gray-300'
                        }
                      `}
                    >
                      <div className={`
                        w-5 h-5 rounded flex items-center justify-center transition-colors
                        ${formData.floor_details.areas_included[option.key]
                          ? 'bg-amber-500 text-white'
                          : 'border-2 border-gray-300'
                        }
                      `}>
                        {formData.floor_details.areas_included[option.key] && <Check className="w-3.5 h-3.5" />}
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.floor_details.areas_included[option.key]}
                        onChange={() => handleFloorAreaChange(option.key)}
                        className="sr-only"
                      />
                      <span className="text-gray-700 text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="additional_areas" className="text-gray-700 font-medium mb-2 block">
                  Additional Areas
                </Label>
                <textarea
                  id="additional_areas"
                  name="additional_areas"
                  value={formData.floor_details.additional_areas}
                  onChange={handleFloorInputChange}
                  className="w-full h-20 px-4 py-3 rounded-xl border border-gray-200 text-sm resize-none"
                  placeholder="e.g., Lift lobbies, plant room corridor..."
                />
              </div>
            </section>

            {/* Service Type */}
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Service Type <span className="text-red-400">*</span></h2>
              <div className="space-y-3">
                {FLOOR_SERVICE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`
                      flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all
                      ${formData.floor_details.service_type === option.value
                        ? 'border-amber-500 bg-amber-500/5'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="service_type"
                      value={option.value}
                      checked={formData.floor_details.service_type === option.value}
                      onChange={handleFloorInputChange}
                      className="sr-only"
                    />
                    <div className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${formData.floor_details.service_type === option.value ? 'border-amber-500' : 'border-gray-300'}
                    `}>
                      {formData.floor_details.service_type === option.value && (
                        <span className="w-3 h-3 rounded-full bg-amber-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{option.label}</span>
                        {option.recommended && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {option.value === 'deep_clean' && 'Basic cleaning to refresh the floor surface'}
                        {option.value === 'strip_polish' && 'Remove old finish and apply new polish - requires annual maintenance'}
                        {option.value === 'pu_coating' && 'Long-lasting protection system - reduced future maintenance costs'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </section>

            {/* Pricing (Internal) */}
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Pricing</h2>
              <p className="text-sm text-gray-500 mb-6">Internal use only - not shown to client</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <Label htmlFor="subcontractor_cost" className="text-gray-700 font-medium mb-2 block">
                    Subcontractor Cost (£) <span className="text-red-400">*</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">£</span>
                    <Input
                      id="subcontractor_cost"
                      name="subcontractor_cost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.floor_details.subcontractor_cost}
                      onChange={handleFloorInputChange}
                      className="h-11 rounded-xl pl-8"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="additional_costs" className="text-gray-700 font-medium mb-2 block">
                    Additional Costs (£)
                  </Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">£</span>
                    <Input
                      id="additional_costs"
                      name="additional_costs"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.floor_details.additional_costs}
                      onChange={handleFloorInputChange}
                      className="h-11 rounded-xl pl-8"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Materials, parking, access fees</p>
                </div>
                <div>
                  <Label htmlFor="labour_days" className="text-gray-700 font-medium mb-2 block">
                    Labour Days <span className="text-gray-400">(optional)</span>
                  </Label>
                  <Input
                    id="labour_days"
                    name="labour_days"
                    type="number"
                    min="0"
                    value={formData.floor_details.labour_days}
                    onChange={handleFloorInputChange}
                    className="h-11 rounded-xl"
                    placeholder="e.g., 3"
                  />
                </div>
              </div>

              {/* Auto-calculated pricing */}
              {floorPricing && floorPricing.totalCost > 0 && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <h3 className="font-medium text-amber-800 mb-3">Calculated Pricing</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-amber-600">Total Cost</p>
                      <p className="text-xl font-bold text-amber-900">£{floorPricing.totalCost.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-amber-600">Suggested Client Price</p>
                      <p className="text-xl font-bold text-amber-900">£{floorPricing.suggestedPrice.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-amber-600">Margin Applied</p>
                      <p className="text-xl font-bold text-amber-900">{floorPricing.marginMin}–{floorPricing.marginMax}%</p>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Notes */}
            <section className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes</h2>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full h-24 px-4 py-3 rounded-xl border border-gray-200 text-sm resize-none"
                placeholder="Any additional notes for this quote..."
              />
            </section>
          </>
        )}

        {/* Service Setup - Show for non-floor quotes */}
        {!isFloor && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Service Setup</h2>
          
          <div className="mb-6">
            <Label className="text-gray-700 font-medium mb-3 block">
              Cleaning frequency <span className="text-red-400">*</span>
            </Label>
            <div className="flex flex-wrap gap-3">
              {currentFrequencyOptions.map((freq) => (
                <label
                  key={freq.value}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors
                    ${formData.cleaning_frequency === freq.value
                      ? 'border-[#1a1f4e] bg-[#1a1f4e]/5 text-[#1a1f4e]'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="cleaning_frequency"
                    value={freq.value}
                    checked={formData.cleaning_frequency === freq.value}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    formData.cleaning_frequency === freq.value ? 'border-[#1a1f4e]' : 'border-gray-300'
                  }`}>
                    {formData.cleaning_frequency === freq.value && (
                      <span className="w-2 h-2 rounded-full bg-[#1a1f4e]" />
                    )}
                  </span>
                  {freq.label}
                </label>
              ))}
            </div>
          </div>
          
          <div>
            <Label className="text-gray-700 font-medium mb-3 block">
              Scope of works {isWindow ? '(Window Cleaning)' : isOffice ? '(Office)' : '(Block)'}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {currentScopeOptions.map((option) => (
                <label
                  key={option.key}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors
                    ${currentScope[option.key]
                      ? 'border-[#00c853] bg-[#00c853]/5'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <div className={`
                    w-5 h-5 rounded flex items-center justify-center transition-colors
                    ${currentScope[option.key]
                      ? 'bg-[#00c853] text-white'
                      : 'border-2 border-gray-300'
                    }
                  `}>
                    {currentScope[option.key] && <Check className="w-3.5 h-3.5" />}
                  </div>
                  <input
                    type="checkbox"
                    checked={currentScope[option.key]}
                    onChange={() => handleScopeChange(option.key, formData.quote_type)}
                    className="sr-only"
                  />
                  <span className="text-gray-700 text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </section>
        )}

        {/* Pricing - Show for non-floor quotes */}
        {!isFloor && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Pricing</h2>
          
          {/* Pricing Type Toggle for Window Cleaning */}
          {isWindow && (
            <div className="mb-6">
              <Label className="text-gray-700 font-medium mb-3 block">Pricing Type</Label>
              <div className="flex gap-4">
                <label
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors
                    ${formData.pricing_type === 'per_clean'
                      ? 'border-[#00c853] bg-[#00c853]/5 text-[#00c853]'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="pricing_type"
                    value="per_clean"
                    checked={formData.pricing_type === 'per_clean'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    formData.pricing_type === 'per_clean' ? 'border-[#00c853]' : 'border-gray-300'
                  }`}>
                    {formData.pricing_type === 'per_clean' && (
                      <span className="w-2 h-2 rounded-full bg-[#00c853]" />
                    )}
                  </span>
                  Per Clean
                </label>
                <label
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors
                    ${formData.pricing_type === 'monthly'
                      ? 'border-[#1a1f4e] bg-[#1a1f4e]/5 text-[#1a1f4e]'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="pricing_type"
                    value="monthly"
                    checked={formData.pricing_type === 'monthly'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    formData.pricing_type === 'monthly' ? 'border-[#1a1f4e]' : 'border-gray-300'
                  }`}>
                    {formData.pricing_type === 'monthly' && (
                      <span className="w-2 h-2 rounded-full bg-[#1a1f4e]" />
                    )}
                  </span>
                  Monthly
                </label>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Per Clean Price (Window only) */}
            {isWindow && formData.pricing_type === 'per_clean' ? (
              <div>
                <Label htmlFor="per_clean_price" className="text-gray-700 font-medium mb-2 block">
                  Price per clean (£) <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">£</span>
                  <Input
                    id="per_clean_price"
                    name="per_clean_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.per_clean_price}
                    onChange={handleInputChange}
                    className="h-11 rounded-xl pl-8"
                    placeholder="0.00"
                    data-testid="quote-per-clean-price"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Price charged for each window cleaning visit
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="monthly_price" className="text-gray-700 font-medium mb-2 block">
                  Monthly price (£) <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">£</span>
                  <Input
                    id="monthly_price"
                    name="monthly_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.monthly_price}
                    onChange={handleInputChange}
                    className="h-11 rounded-xl pl-8"
                    placeholder="0.00"
                    data-testid="quote-price"
                  />
                </div>
              </div>
            )}
            
            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className={`
                  w-5 h-5 rounded flex items-center justify-center transition-colors
                  ${formData.vat_registered
                    ? 'bg-[#1a1f4e] text-white'
                    : 'border-2 border-gray-300'
                  }
                `}>
                  {formData.vat_registered && <Check className="w-3.5 h-3.5" />}
                </div>
                <input
                  type="checkbox"
                  name="vat_registered"
                  checked={formData.vat_registered}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <span className="text-gray-700">VAT registered (add VAT to price)</span>
              </label>
            </div>
          </div>
          
          <p className="text-sm text-gray-500 mt-4">
            {formData.vat_registered 
              ? "VAT will be added to the quoted price" 
              : "Quote will show: 'VAT not applicable (not VAT registered)'"}
          </p>
        </section>
        )}

        {/* Terms - Show for non-floor quotes */}
        {!isFloor && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Terms</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label className="text-gray-700 font-medium mb-3 block">Contract type</Label>
              <select
                name="contract_type"
                value={formData.contract_type}
                onChange={handleInputChange}
                className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none bg-white"
              >
                <option value="rolling_monthly">Rolling monthly</option>
                <option value="3_month">3-month minimum</option>
                <option value="6_month">6-month minimum</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="notice_period" className="text-gray-700 font-medium mb-2 block">
                Notice period (days)
              </Label>
              <Input
                id="notice_period"
                name="notice_period"
                type="number"
                min="0"
                value={formData.notice_period}
                onChange={handleInputChange}
                className="h-11 rounded-xl"
              />
            </div>
            
            <div>
              <Label htmlFor="start_timeframe" className="text-gray-700 font-medium mb-2 block">
                Start timeframe
              </Label>
              <Input
                id="start_timeframe"
                name="start_timeframe"
                value={formData.start_timeframe}
                onChange={handleInputChange}
                className="h-11 rounded-xl"
                placeholder="e.g., within 7–14 days"
              />
            </div>
          </div>
        </section>
        )}

        {/* Optional Notes - Show for non-floor quotes */}
        {!isFloor && (
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Optional Notes</h2>
          
          <div>
            <Label htmlFor="notes" className="text-gray-700 font-medium mb-2 block">
              Assumptions / notes <span className="text-gray-400 font-normal">(max 300 characters)</span>
            </Label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              maxLength={300}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1a1f4e] focus:ring-2 focus:ring-[#1a1f4e]/10 outline-none resize-none"
              placeholder="Any assumptions or additional notes for this quote..."
            />
            <p className="text-xs text-gray-400 mt-2">{formData.notes.length}/300</p>
          </div>
        </section>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Submit */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin')}
            className="h-12 px-6 rounded-xl"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-[#00c853] hover:bg-[#00a844] text-white h-12 px-8 rounded-xl font-medium"
            data-testid="submit-quote-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Create {isFloor ? 'Floor Restoration' : isWindow ? 'Window Cleaning' : isOffice ? 'Office' : 'Block'} Quote
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default QuoteCreate;
